import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  ThumbUp,
  Favorite,
  InsertEmoticon,
  EmojiEmotions,
  SentimentDissatisfied,
  Mood,
  Comment,
  Share,
  TrendingUp,
  TrendingDown,
  Refresh,
  Facebook,
  CalendarToday,
  BarChart,
  Close,
} from '@mui/icons-material';
import { fetchFacebookAnalytics } from '../../Action/socialMediaAPI';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSettingsAccounts } from '../../store/settingsSlice';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const FacebookAnalytics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  // Get Facebook accounts from Redux store
  const accounts = useSelector((state) => state.settings.accounts);
  const accountsStatus = useSelector((state) => state.settings.accountsStatus);
  
  const facebookAccounts = useMemo(() => {
    console.log('All accounts from Redux:', accounts);
    const fbAccounts = accounts.filter(
      (acc) =>
        acc.provider?.toLowerCase() === 'facebook' &&
        acc.status === 'Active' &&
        acc.accessToken
    );
    console.log('Filtered Facebook accounts:', fbAccounts);
    return fbAccounts;
  }, [accounts]);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageDialog, setImageDialog] = useState({ open: false, url: '', message: '' });

  // Load accounts from Redux on component mount
  useEffect(() => {
    console.log('FacebookAnalytics mounted, accountsStatus:', accountsStatus);
    if (accountsStatus === 'idle') {
      console.log('Dispatching fetchSettingsAccounts...');
      dispatch(fetchSettingsAccounts());
    }
  }, [dispatch, accountsStatus]);

  // Set default account when Facebook accounts load
  useEffect(() => {
    console.log('Facebook accounts changed:', facebookAccounts.length);
    if (facebookAccounts.length > 0 && !selectedAccount) {
      console.log('Setting default account:', facebookAccounts[0]);
      setSelectedAccount(facebookAccounts[0]);
    }
  }, [facebookAccounts, selectedAccount]);

  // Fetch analytics when account changes
  const fetchAnalytics = useCallback(async () => {
    if (!selectedAccount?.username || !selectedAccount?.accessToken) {
      console.warn('Cannot fetch analytics - missing username/pageId or accessToken', selectedAccount);
      setError('Please select a valid Facebook account');
      return;
    }

    console.log('Fetching Facebook analytics for:', {
      pageId: selectedAccount.username,
      accountName: selectedAccount.accountName,
      hasAccessToken: !!selectedAccount.accessToken
    });

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFacebookAnalytics(
        selectedAccount.username,
        selectedAccount.accessToken
      );

      console.log('Facebook analytics result:', result);

      if (result.success) {
        // Extract the data array from the first element if it's nested
        const data = Array.isArray(result.data) && result.data.length > 0 && result.data[0].data
          ? result.data[0].data
          : result.data;
        console.log('Setting analytics data:', data);
        setAnalyticsData(data || []);
      } else {
        console.error('Failed to load analytics:', result.message);
        setError(result.message || 'Failed to load analytics');
        setAnalyticsData([]);
      }
    } catch (err) {
      console.error('Error fetching Facebook analytics:', err);
      setError('An error occurred while fetching analytics');
      setAnalyticsData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount]);

  useEffect(() => {
    console.log('Selected account changed:', selectedAccount);
    if (selectedAccount?.username && selectedAccount?.accessToken) {
      console.log('Calling fetchAnalytics...');
      fetchAnalytics();
    }
  }, [selectedAccount, fetchAnalytics]);

  const handleAccountChange = (event) => {
    const account = facebookAccounts.find((acc) => acc.accountId === event.target.value);
    console.log('Account changed to:', account);
    setSelectedAccount(account);
  };

  const handleRefresh = () => {
    console.log('Refresh button clicked');
    fetchAnalytics();
  };

  const openImageDialog = (url, message) => {
    setImageDialog({ open: true, url, message });
  };

  const closeImageDialog = () => {
    setImageDialog({ open: false, url: '', message: '' });
  };

  // Calculate aggregate statistics
  const stats = useMemo(() => {
    if (!analyticsData || analyticsData.length === 0) {
      return {
        totalPosts: 0,
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalReactions: 0,
        avgEngagement: 0,
        loveCount: 0,
        hahaCount: 0,
        wowCount: 0,
        sadCount: 0,
        angryCount: 0,
        careCount: 0,
      };
    }

    const totals = analyticsData.reduce((acc, post) => {
      return {
        totalLikes: acc.totalLikes + (post.like_count?.summary?.total_count || 0),
        totalComments: acc.totalComments + (post.comments?.summary?.total_count || 0),
        totalShares: acc.totalShares + (post.shares?.count || 0),
        totalReactions: acc.totalReactions + (post.reactions?.summary?.total_count || 0),
        loveCount: acc.loveCount + (post.love_count?.summary?.total_count || 0),
        hahaCount: acc.hahaCount + (post.haha_count?.summary?.total_count || 0),
        wowCount: acc.wowCount + (post.wow_count?.summary?.total_count || 0),
        sadCount: acc.sadCount + (post.sad_count?.summary?.total_count || 0),
        angryCount: acc.angryCount + (post.angry_count?.summary?.total_count || 0),
        careCount: acc.careCount + (post.care_count?.summary?.total_count || 0),
      };
    }, {
      totalLikes: 0,
      totalComments: 0,
      totalShares: 0,
      totalReactions: 0,
      loveCount: 0,
      hahaCount: 0,
      wowCount: 0,
      sadCount: 0,
      angryCount: 0,
      careCount: 0,
    });

    const avgEngagement = analyticsData.length > 0
      ? (totals.totalReactions + totals.totalComments + totals.totalShares) / analyticsData.length
      : 0;

    return {
      totalPosts: analyticsData.length,
      ...totals,
      avgEngagement: Math.round(avgEngagement * 10) / 10,
    };
  }, [analyticsData]);

  // Prepare chart data for visualizations
  const chartData = useMemo(() => {
    if (!analyticsData || analyticsData.length === 0) {
      return {
        engagementTrend: null,
        metricsComparison: null,
        reactionDistribution: null,
        topPosts: null,
      };
    }

    // Sort posts by date for timeline
    const sortedPosts = [...analyticsData].sort((a, b) => 
      new Date(a.created_time) - new Date(b.created_time)
    );

    // 1. Engagement Trend Over Time (Line Chart)
    const engagementTrend = {
      labels: sortedPosts.map((post) => {
        const date = new Date(post.created_time);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }).slice(-10), // Show last 10 posts for clarity
      datasets: [
        {
          label: 'Reactions',
          data: sortedPosts.map((post) => post.reactions?.summary?.total_count || 0).slice(-10),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgb(59, 130, 246)',
          pointHoverBorderWidth: 3,
        },
        {
          label: 'Comments',
          data: sortedPosts.map((post) => post.comments?.summary?.total_count || 0).slice(-10),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.2)',
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgb(16, 185, 129)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgb(16, 185, 129)',
          pointHoverBorderWidth: 3,
        },
        {
          label: 'Shares',
          data: sortedPosts.map((post) => post.shares?.count || 0).slice(-10),
          borderColor: 'rgb(245, 158, 11)',
          backgroundColor: 'rgba(245, 158, 11, 0.2)',
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgb(245, 158, 11)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgb(245, 158, 11)',
          pointHoverBorderWidth: 3,
        },
      ],
    };

    // 2. Total Metrics Comparison (Bar Chart)
    const metricsComparison = {
      labels: ['Reactions', 'Comments', 'Shares'],
      datasets: [
        {
          label: 'Total Count',
          data: [stats.totalReactions, stats.totalComments, stats.totalShares],
          backgroundColor: [
            'rgba(59, 130, 246, 0.85)',
            'rgba(16, 185, 129, 0.85)',
            'rgba(245, 158, 11, 0.85)',
          ],
          borderColor: [
            'rgb(59, 130, 246)',
            'rgb(16, 185, 129)',
            'rgb(245, 158, 11)',
          ],
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };

    // 3. Reaction Type Distribution (Doughnut Chart)
    const reactionDistribution = {
      labels: ['Like', 'Love', 'Haha', 'Wow', 'Sad', 'Care'],
      datasets: [
        {
          data: [
            stats.totalLikes,
            stats.loveCount,
            stats.hahaCount,
            stats.wowCount,
            stats.sadCount,
            stats.careCount,
          ],
          backgroundColor: [
            'rgba(24, 119, 242, 0.85)',
            'rgba(249, 24, 128, 0.85)',
            'rgba(247, 177, 37, 0.85)',
            'rgba(139, 92, 246, 0.85)',
            'rgba(234, 88, 12, 0.85)',
            'rgba(233, 113, 15, 0.85)',
          ],
          borderColor: [
            'rgb(24, 119, 242)',
            'rgb(249, 24, 128)',
            'rgb(247, 177, 37)',
            'rgb(139, 92, 246)',
            'rgb(234, 88, 12)',
            'rgb(233, 113, 15)',
          ],
          borderWidth: 3,
          hoverOffset: 15,
          hoverBorderWidth: 4,
        },
      ],
    };

    // 4. Top Performing Posts (Horizontal Bar Chart)
    const topPostsData = sortedPosts
      .map((post, index) => ({
        index,
        message: post.message?.substring(0, 30) + '...' || `Post ${index + 1}`,
        engagement:
          (post.reactions?.summary?.total_count || 0) +
          (post.comments?.summary?.total_count || 0) +
          (post.shares?.count || 0),
      }))
      .sort((a, b) => b.engagement - a.engagement)
      .slice(0, 5); // Top 5 posts

    const topPosts = {
      labels: topPostsData.map((post) => post.message),
      datasets: [
        {
          label: 'Total Engagement',
          data: topPostsData.map((post) => post.engagement),
          backgroundColor: 'rgba(139, 92, 246, 0.85)',
          borderColor: 'rgb(139, 92, 246)',
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };

    return {
      engagementTrend,
      metricsComparison,
      reactionDistribution,
      topPosts,
    };
  }, [analyticsData, stats]);

  // Chart options with enhanced styling
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 13,
            weight: '600',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 13,
            weight: '600',
          },
        },
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 13,
            weight: '600',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  const topPostsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // If no Facebook accounts
  if (facebookAccounts.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Facebook sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
          <Typography variant="h5" gutterBottom>
            No Facebook Accounts Connected
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Connect a Facebook account in Settings to view analytics
          </Typography>
          <Button
            variant="contained"
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}
            onClick={() => window.location.href = '/settings'}
          >
            Go to Settings
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: '#1877f2',
                  width: 56,
                  height: 56,
                }}
              >
                <Facebook sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" color="text.primary">
                  Facebook Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track your Facebook page performance and engagement
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl 
              fullWidth 
              variant="outlined" 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(24, 119, 242, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(24, 119, 242, 0.15)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1877f2',
                    },
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 16px rgba(24, 119, 242, 0.2)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1877f2',
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#1877f2',
                  },
                },
              }}
            >
              <InputLabel>Select Account</InputLabel>
              <Select
                value={selectedAccount?.accountId || ''}
                onChange={handleAccountChange}
                label="Select Account"
              >
                {facebookAccounts.map((account) => (
                  <MenuItem 
                    key={account.accountId} 
                    value={account.accountId}
                    sx={{
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(24, 119, 242, 0.08)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(24, 119, 242, 0.12)',
                        '&:hover': {
                          backgroundColor: 'rgba(24, 119, 242, 0.16)',
                        },
                      },
                    }}
                  >
                    <Facebook sx={{ fontSize: 20, color: '#1877f2' }} />
                    <span>{account.accountName || account.username || account.accountId}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<Refresh sx={{ 
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }} />}
              onClick={handleRefresh}
              disabled={loading || !selectedAccount}
              fullWidth
              size="medium"
              sx={{
                background: 'linear-gradient(135deg, #1877f2 0%, #0a4d92 100%)',
                color: '#fff',
                fontWeight: 'bold',
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(24, 119, 242, 0.3)',
                textTransform: 'none',
                fontSize: '0.95rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0a4d92 0%, #1877f2 100%)',
                  boxShadow: '0 6px 20px rgba(24, 119, 242, 0.4)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 2px 8px rgba(24, 119, 242, 0.3)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                  color: '#9e9e9e',
                  boxShadow: 'none',
                },
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Analytics Dashboard */}
      {!loading && !error && analyticsData.length > 0 && (
        <>
          {/* Overview Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Total Posts</Typography>
                    <BarChart />
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {stats.totalPosts}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Published content
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Total Reactions</Typography>
                    <EmojiEmotions />
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {stats.totalReactions}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    All reaction types
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Total Comments</Typography>
                    <Comment />
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {stats.totalComments}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Conversations started
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={3}
                sx={{
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  color: 'white',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Avg Engagement</Typography>
                    <TrendingUp />
                  </Box>
                  <Typography variant="h3" fontWeight="bold">
                    {stats.avgEngagement}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Per post
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Chart Visualizations for Management Insights */}
          {chartData.engagementTrend && (
            <>
              {/* Section Header with styling */}
              <Box
                sx={{
                  mt: 5,
                  mb: 4,
                  p: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="white"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  📊 Analytics Dashboard - Management Insights
                </Typography>
                <Typography variant="body1" color="white" sx={{ opacity: 0.9, mt: 1 }}>
                  Comprehensive visual analysis of your Facebook page performance
                </Typography>
              </Box>

              {/* Row 1: Engagement Trend Line Chart */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(102, 126, 234, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(59, 130, 246, 0.1)',
                          color: 'rgb(59, 130, 246)',
                          width: 48,
                          height: 48,
                        }}
                      >
                        <TrendingUp sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                          Engagement Trends Over Time
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Track reactions, comments, and shares across your posts
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ height: 400 }}>
                      <Line data={chartData.engagementTrend} options={lineChartOptions} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Row 2: Metrics Comparison and Reaction Distribution */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #ffffff 0%, #fff5f5 100%)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(16, 185, 129, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(16, 185, 129, 0.1)',
                          color: 'rgb(16, 185, 129)',
                          width: 48,
                          height: 48,
                        }}
                      >
                        <BarChart sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                          Total Metrics Comparison
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Compare overall engagement metrics
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ height: 400 }}>
                      <Bar data={chartData.metricsComparison} options={barChartOptions} />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #ffffff 0%, #fffbf5 100%)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(249, 24, 128, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(249, 24, 128, 0.1)',
                          color: 'rgb(249, 24, 128)',
                          width: 48,
                          height: 48,
                        }}
                      >
                        <EmojiEmotions sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                          Reaction Type Distribution
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Emotional response breakdown
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ height: 400 }}>
                      <Doughnut
                        data={chartData.reactionDistribution}
                        options={doughnutChartOptions}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Row 3: Top Performing Posts */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #ffffff 0%, #fcf5ff 100%)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(139, 92, 246, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(139, 92, 246, 0.1)',
                          color: 'rgb(139, 92, 246)',
                          width: 48,
                          height: 48,
                        }}
                      >
                        <TrendingUp sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                          Top 5 Performing Posts
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Identify your most engaging content
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ height: 350 }}>
                      <Bar data={chartData.topPosts} options={topPostsChartOptions} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          {/* Reaction Breakdown */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Reaction Breakdown
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <ThumbUp sx={{ fontSize: 40, color: '#1877f2', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {stats.totalLikes}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Likes
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <Favorite sx={{ fontSize: 40, color: '#f91880', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {stats.loveCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Love
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <InsertEmoticon sx={{ fontSize: 40, color: '#f7b125', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {stats.hahaCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Haha
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <Mood sx={{ fontSize: 40, color: '#f7b125', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {stats.wowCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wow
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <SentimentDissatisfied sx={{ fontSize: 40, color: '#f7b125', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {stats.sadCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sad
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <EmojiEmotions sx={{ fontSize: 40, color: '#e9710f', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {stats.careCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Care
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Posts List */}
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Recent Posts ({analyticsData.length})
          </Typography>
          <Grid container spacing={3}>
            {analyticsData.map((post, index) => {
              const postReactions = post.reactions?.summary?.total_count || 0;
              const postComments = post.comments?.summary?.total_count || 0;
              const postShares = post.shares?.count || 0;
              const postEngagement = postReactions + postComments + postShares;

              return (
                <Grid item xs={12} key={post.id || index}>
                  <Card elevation={3} sx={{ '&:hover': { boxShadow: 6 } }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        {/* Post Image */}
                        {post.full_picture && (
                          <Grid item xs={12} md={3}>
                            <Box
                              component="img"
                              src={post.full_picture}
                              alt="Post"
                              sx={{
                                width: '100%',
                                height: isMobile ? 200 : 180,
                                objectFit: 'cover',
                                borderRadius: 2,
                                cursor: 'pointer',
                              }}
                              onClick={() => openImageDialog(post.full_picture, post.message)}
                            />
                          </Grid>
                        )}

                        {/* Post Content */}
                        <Grid item xs={12} md={post.full_picture ? 9 : 12}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(post.created_time)}
                              </Typography>
                            </Box>
                            <Chip
                              label={`Engagement: ${postEngagement}`}
                              size="small"
                              color={postEngagement > stats.avgEngagement ? 'success' : 'default'}
                              icon={postEngagement > stats.avgEngagement ? <TrendingUp /> : <TrendingDown />}
                            />
                          </Box>

                          <Typography
                            variant="body1"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {post.message || 'No message content'}
                          </Typography>

                          {/* Engagement Metrics */}
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <EmojiEmotions sx={{ fontSize: 20, color: '#1877f2' }} />
                                  <Typography variant="h6" fontWeight="bold">
                                    {postReactions}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Reactions
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <Comment sx={{ fontSize: 20, color: '#00a6ed' }} />
                                  <Typography variant="h6" fontWeight="bold">
                                    {postComments}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Comments
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <Share sx={{ fontSize: 20, color: '#10b981' }} />
                                  <Typography variant="h6" fontWeight="bold">
                                    {postShares}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Shares
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <ThumbUp sx={{ fontSize: 20, color: '#f59e0b' }} />
                                  <Typography variant="h6" fontWeight="bold">
                                    {post.like_count?.summary?.total_count || 0}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Likes
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Reaction Details */}
                          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                              {post.love_count?.summary?.total_count > 0 && (
                                <Chip
                                  size="small"
                                  icon={<Favorite sx={{ fontSize: 16 }} />}
                                  label={post.love_count.summary.total_count}
                                />
                              )}
                              {post.haha_count?.summary?.total_count > 0 && (
                                <Chip
                                  size="small"
                                  icon={<InsertEmoticon sx={{ fontSize: 16 }} />}
                                  label={post.haha_count.summary.total_count}
                                />
                              )}
                              {post.wow_count?.summary?.total_count > 0 && (
                                <Chip
                                  size="small"
                                  icon={<Mood sx={{ fontSize: 16 }} />}
                                  label={post.wow_count.summary.total_count}
                                />
                              )}
                              {post.sad_count?.summary?.total_count > 0 && (
                                <Chip
                                  size="small"
                                  icon={<SentimentDissatisfied sx={{ fontSize: 16 }} />}
                                  label={post.sad_count.summary.total_count}
                                />
                              )}
                              {post.care_count?.summary?.total_count > 0 && (
                                <Chip
                                  size="small"
                                  icon={<EmojiEmotions sx={{ fontSize: 16 }} />}
                                  label={post.care_count.summary.total_count}
                                />
                              )}
                            </Stack>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* No Data State */}
      {!loading && !error && analyticsData.length === 0 && (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <BarChart sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Analytics Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This Facebook page doesn't have any posts yet, or there was an issue fetching the data.
          </Typography>
        </Paper>
      )}

      {/* Image Dialog */}
      <Dialog
        open={imageDialog.open}
        onClose={closeImageDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Post Details</Typography>
            <IconButton onClick={closeImageDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {imageDialog.url && (
            <Box
              component="img"
              src={imageDialog.url}
              alt="Post"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                mb: 2,
              }}
            />
          )}
          {imageDialog.message && (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {imageDialog.message}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default FacebookAnalytics;
